(() => {
  "use strict";

  const SELECTOR = "[data-account-hub-root]";
  const MAX_VISIBLE_ITEMS = 4;
  const REQUEST_TIMEOUT_MS = 15000;

  function query(root, selector) {
    return root.querySelector(selector);
  }

  function setText(root, selector, value) {
    const node = query(root, selector);
    if (node) node.textContent = String(value ?? "");
  }

  function setHidden(node, hidden) {
    if (node) node.hidden = Boolean(hidden);
  }

  function safePath(value, fallback) {
    const candidate = String(value || "").trim();
    if (
      !candidate.startsWith("/") ||
      candidate.startsWith("//") ||
      candidate.includes("\\") ||
      /[\u0000-\u001f\u007f]/.test(candidate)
    ) return fallback;

    try {
      const parsed = new URL(candidate, window.location.origin);
      if (parsed.origin !== window.location.origin) return fallback;
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch {
      return fallback;
    }
  }

  function setSafeLink(root, selector, value, fallback) {
    const link = query(root, selector);
    if (link) link.setAttribute("href", safePath(value, fallback));
  }

  function formatCount(value) {
    const count = Number(value);
    return Number.isFinite(count) && count > 0 ? Math.floor(count).toLocaleString() : "0";
  }

  function formatStatus(value) {
    return String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[_-]+/g, " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  }

  function formatDate(value, includeTime = true) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const options = includeTime
      ? { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }
      : { month: "short", day: "numeric", year: "numeric" };
    return new Intl.DateTimeFormat(undefined, options).format(date);
  }

  function refreshIcons() {
    if (window.lucide && typeof window.lucide.createIcons === "function") {
      window.lucide.createIcons();
    }
  }

  function createListItem({ title, detail, status, dateTime }) {
    const item = document.createElement("li");
    const copy = document.createElement("div");
    const heading = document.createElement("strong");
    heading.textContent = title || "Darkstone activity";
    copy.appendChild(heading);

    if (detail) {
      const meta = dateTime ? document.createElement("time") : document.createElement("small");
      meta.textContent = detail;
      if (dateTime) meta.setAttribute("datetime", dateTime);
      copy.appendChild(meta);
    }

    item.appendChild(copy);
    if (status) {
      const badge = document.createElement("span");
      badge.className = "dsh-account__item-status";
      badge.textContent = formatStatus(status);
      item.appendChild(badge);
    }
    return item;
  }

  function renderItems(list, empty, items) {
    if (!list) return;
    list.replaceChildren();
    const visible = Array.isArray(items) ? items.slice(0, MAX_VISIBLE_ITEMS) : [];
    visible.forEach((item) => list.appendChild(createListItem(item)));
    setHidden(list, visible.length === 0);
    setHidden(empty, visible.length !== 0);
  }

  function buildPullItems(data) {
    const items = Array.isArray(data?.pullList?.activeItems) ? data.pullList.activeItems : [];
    return items.map((item) => ({
      title: `${item.title || "Pull-list title"}${Number(item.quantity) > 1 ? ` × ${Number(item.quantity)}` : ""}`,
      detail: item.publisher || (item.kind === "SERIES" ? "Series subscription" : "Issue request"),
      status: item.kind === "SERIES" ? "series" : "issue",
    }));
  }

  function buildReservationItems(data) {
    const items = Array.isArray(data?.reservations) ? data.reservations : [];
    return items.map((item) => {
      const holdDate = formatDate(item.holdUntil, false);
      return {
        title: `${item.title || "Reserved item"}${Number(item.quantity) > 1 ? ` × ${Number(item.quantity)}` : ""}`,
        detail: holdDate ? `Held through ${holdDate}` : "Reserved for pickup",
        status: item.status || "open",
        dateTime: item.holdUntil || null,
      };
    });
  }

  function buildTicketItems(data) {
    const items = Array.isArray(data?.tickets) ? data.tickets : [];
    return items.map((item) => {
      const when = formatDate(item.startsAt, true);
      const detail = [when, item.location].filter(Boolean).join(" · ");
      return {
        title: item.eventTitle || "Darkstone event",
        detail: detail || "Upcoming event",
        status: item.status || "active",
        dateTime: item.startsAt || null,
      };
    });
  }

  function buildGameFinderItems(data) {
    const gameFinder = data?.gameFinder || {};
    const requests = Array.isArray(gameFinder.activeRequests) ? gameFinder.activeRequests : [];
    const responses = Array.isArray(gameFinder.recentResponses) ? gameFinder.recentResponses : [];
    const result = [];

    requests.slice(0, 2).forEach((request) => {
      const when = formatDate(request.startsAt, true);
      result.push({
        title: request.title || "Game request",
        detail: [request.gameSystem, when].filter(Boolean).join(" · "),
        status: request.status || "open",
        dateTime: request.startsAt || null,
      });
    });

    responses.slice(0, 2).forEach((response) => {
      const when = formatDate(response.startsAt, true);
      result.push({
        title: response.requestTitle || "Game response",
        detail: [response.gameSystem, when].filter(Boolean).join(" · "),
        status: response.status || "interested",
        dateTime: response.startsAt || null,
      });
    });

    return result;
  }

  function validateHubPayload(data) {
    const isObject = (value) => Boolean(value) && typeof value === "object" && !Array.isArray(value);
    const isCount = (value) => typeof value === "number" && Number.isFinite(value) && value >= 0;
    const valid =
      isObject(data) &&
      data.version === 2 &&
      isObject(data.customer) &&
      isObject(data.guild) &&
      isCount(data.guild.availablePoints) &&
      isCount(data.guild.reservedPoints) &&
      isObject(data.totals) &&
      isCount(data.totals.pullListItems) &&
      isCount(data.totals.reservations) &&
      isCount(data.totals.upcomingTickets) &&
      isObject(data.pullList) &&
      isCount(data.pullList.activeCount) &&
      Array.isArray(data.pullList.activeItems) &&
      Array.isArray(data.reservations) &&
      Array.isArray(data.tickets) &&
      isObject(data.gameFinder) &&
      isCount(data.gameFinder.activeRequestCount) &&
      isCount(data.gameFinder.confirmedMatchCount) &&
      Array.isArray(data.gameFinder.activeRequests) &&
      Array.isArray(data.gameFinder.recentResponses) &&
      isObject(data.links);

    if (!valid) {
      throw new Error("Your account returned incomplete data. Please try again.");
    }
    return data;
  }

  function renderDashboard(root, data) {
    const firstName = String(data?.customer?.firstName || "").trim();
    const displayName = String(data?.customer?.displayName || "Guild Member").trim();
    setText(root, "[data-account-hub-greeting]", `Welcome back, ${firstName || displayName}.`);

    const generated = formatDate(data?.generatedAt, true);
    setText(root, "[data-account-hub-updated]", generated ? `Updated ${generated}` : "Your account tools are ready.");

    const points = Number(data?.guild?.availablePoints) || 0;
    const reservedPoints = Number(data?.guild?.reservedPoints) || 0;
    const pullCount = Number(data.totals.pullListItems);
    const reservationCount = Number(data.totals.reservations);
    const ticketCount = Number(data.totals.upcomingTickets);

    setText(root, "[data-account-hub-points]", formatCount(points));
    setText(root, "[data-account-hub-points-large]", formatCount(points));
    setText(root, "[data-account-hub-pull-count]", formatCount(pullCount));
    setText(root, "[data-account-hub-pull-count-inline]", formatCount(pullCount));
    setText(root, "[data-account-hub-reservation-count]", formatCount(reservationCount));
    setText(root, "[data-account-hub-reservation-count-inline]", formatCount(reservationCount));
    setText(root, "[data-account-hub-ticket-count]", formatCount(ticketCount));
    setText(root, "[data-account-hub-ticket-count-inline]", formatCount(ticketCount));

    setText(root, "[data-account-hub-guild-status]", data?.guild?.enrolled ? "Guild member" : "Not enrolled");
    setText(
      root,
      "[data-account-hub-reserved-points]",
      reservedPoints > 0
        ? `${formatCount(reservedPoints)} Coins reserved for active checkout activity.`
        : "No Coins currently reserved.",
    );

    const memberCodeWrap = query(root, "[data-account-hub-member-code-wrap]");
    const memberCode = String(data?.guild?.barcode || "").trim();
    setText(root, "[data-account-hub-member-code]", memberCode);
    setHidden(memberCodeWrap, !memberCode);

    renderItems(
      query(root, "[data-account-hub-pull-list]"),
      query(root, "[data-account-hub-pull-empty]"),
      buildPullItems(data),
    );
    renderItems(
      query(root, "[data-account-hub-reservations]"),
      query(root, "[data-account-hub-reservations-empty]"),
      buildReservationItems(data),
    );
    renderItems(
      query(root, "[data-account-hub-tickets]"),
      query(root, "[data-account-hub-tickets-empty]"),
      buildTicketItems(data),
    );

    const gameFinder = data?.gameFinder || {};
    const requestCount = Number(gameFinder.activeRequestCount) || 0;
    const confirmedCount = Number(gameFinder.confirmedMatchCount) || 0;
    setText(root, "[data-account-hub-game-requests]", formatCount(requestCount));
    setText(root, "[data-account-hub-game-confirmed]", formatCount(confirmedCount));
    setText(
      root,
      "[data-account-hub-game-finder-status]",
      gameFinder.paused ? "Paused" : gameFinder.enrolled ? "Profile active" : "Get started",
    );
    setText(
      root,
      "[data-account-hub-game-copy]",
      gameFinder.paused
        ? "Your profile is paused. Open Game Finder whenever you're ready to return."
        : gameFinder.enrolled
          ? "Your player profile connects requests, responses, and confirmed games."
          : "Create a player profile to find opponents and schedule games at Darkstone.",
    );
    renderItems(query(root, "[data-account-hub-game-activity]"), null, buildGameFinderItems(data));

    const links = data?.links || {};
    setSafeLink(root, "[data-account-hub-guild-link]", links.guildLedger, "/apps/portal/guild-ledger");
    setSafeLink(root, "[data-account-hub-pull-link]", links.pullList, "/pages/pull-list");
    setSafeLink(root, "[data-account-hub-events-link]", links.events, "/pages/events");
    setSafeLink(root, "[data-account-hub-game-link]", links.gameFinder, "/apps/portal/game-finder");
    setSafeLink(root, "[data-account-hub-tcg-link]", links.tcgPortal, "/pages/tcg-singles");
    setSafeLink(root, "[data-account-hub-commission-link]", links.commissionBoard, "/apps/portal/commission-board");
    setSafeLink(root, "[data-account-hub-paint-link]", links.paintGuild, "/apps/portal/paint-guild");
  }

  function initialize(root) {
    if (!root || root.dataset.accountHubInitialized === "true") return;
    root.dataset.accountHubInitialized = "true";
    if (root.dataset.accountHubPageTitle) document.title = root.dataset.accountHubPageTitle;
    document.querySelectorAll("[data-my-darkstone-nav]").forEach((link) => {
      link.classList.add("is-active");
      link.setAttribute("aria-current", "page");
    });
    refreshIcons();

    if (root.dataset.accountHubAuthenticated !== "true") return;

    const loading = query(root, "[data-account-hub-loading]");
    const errorPanel = query(root, "[data-account-hub-error]");
    const errorMessage = query(root, "[data-account-hub-error-message]");
    const content = query(root, "[data-account-hub-content]");
    const retry = query(root, "[data-account-hub-retry]");
    let controller = null;

    async function load() {
      if (controller) controller.abort();
      const requestController = new AbortController();
      const manageFocus = Boolean(errorPanel?.contains(document.activeElement));
      controller = requestController;
      const timeout = window.setTimeout(() => requestController.abort(), REQUEST_TIMEOUT_MS);
      root.setAttribute("aria-busy", "true");
      setHidden(loading, false);
      if (manageFocus && loading) loading.focus();
      setHidden(errorPanel, true);
      setHidden(content, true);

      try {
        const response = await fetch(root.dataset.accountHubEndpoint || "/apps/portal/account-hub", {
          credentials: "same-origin",
          headers: { Accept: "application/json" },
          signal: requestController.signal,
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.error || `Unable to load your account (${response.status}).`);
        }
        const data = validateHubPayload(payload);
        if (controller !== requestController) return;
        renderDashboard(root, data);
        setHidden(loading, true);
        setHidden(errorPanel, true);
        setHidden(content, false);
        root.setAttribute("aria-busy", "false");
        if (manageFocus && content) content.focus();
        refreshIcons();
      } catch (error) {
        if (controller !== requestController) return;
        if (error?.name === "AbortError") {
          if (errorMessage) errorMessage.textContent = "The account request took too long. Please try again.";
        } else if (errorMessage) {
          errorMessage.textContent = error?.message || "Your account is temporarily unavailable.";
        }
        setHidden(loading, true);
        setHidden(content, true);
        setHidden(errorPanel, false);
        root.setAttribute("aria-busy", "false");
        if (manageFocus && errorPanel) errorPanel.focus();
        refreshIcons();
      } finally {
        window.clearTimeout(timeout);
        if (controller === requestController) controller = null;
      }
    }

    if (retry) retry.addEventListener("click", load);
    window.addEventListener("pagehide", () => controller?.abort(), { once: true });
    load();
  }

  function start() {
    document.querySelectorAll(SELECTOR).forEach(initialize);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }

  document.addEventListener("shopify:section:load", (event) => {
    const root = event.target?.matches?.(SELECTOR) ? event.target : event.target?.querySelector?.(SELECTOR);
    if (root) initialize(root);
  });
})();
